import { useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import ChippedMultiSelect from 'components/shared/ChippedMultiSelect';
import StandardModal from 'components/shared/StandardModal';
import { ClassificationApiName } from 'models/Classification';
import Classification from 'models/Classification';
import { classificationsQueryKeys, useInfiniteClassifications } from 'services/api/classification';
import { clientClassificationsQueryKeys, updateClientClassifications } from 'services/api/clientClassifications';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

type AddAcceptedPlanProps = {
  setShow: (show: boolean) => void;
  clientId: string;
};

const AddAcceptedPlanModal = ({ setShow, clientId }: AddAcceptedPlanProps) => {
  const [selectedPlans, setSelectedPlans] = useState<Classification[]>([]);
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const asyncPlanOptionsParams = {
    pageSize: 10,
    page: 1,
    sortBy: 'name asc',
    type: ClassificationApiName.HEALTH_PLAN,
    'client.not': clientId,
  };

  const asyncPlanOptions = useInfiniteClassifications(asyncPlanOptionsParams);

  const { mutate: addAcceptedPlans, isPending } = useMutation({
    mutationFn: () => {
      return updateClientClassifications({
        classificationIds: selectedPlans.map((sp) => sp.id),
        classificationType: ClassificationApiName.HEALTH_PLAN,
      });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: clientClassificationsQueryKeys.index(),
      });

      queryClient.resetQueries({
        queryKey: classificationsQueryKeys.index({
          'client.not': clientId,
        }),
      });

      setShow(false);
    },
    onError: () => {
      addToast({
        text: `Something went wrong. Please try again.`,
      });
    },
  });

  const handleAdd = () => {
    addAcceptedPlans();
  };

  return (
    <StandardModal title='Add Contracted Health Plans' onCancel={() => setShow(false)}>
      <ModalContent>
        <BodySmall>
          Olio will indicate which health plans you are contracted with to your referral partners. Referrals can still
          be submitted for health plans that you are not contracted with.
        </BodySmall>
        <ChippedMultiSelect<Classification>
          {...asyncPlanOptions}
          value={selectedPlans}
          onChange={setSelectedPlans}
          placeholder='Search Health Plans'
          inputLabel='Contracted Health Plans'
          containerLabel='Selected Health Plans'
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
        />
      </ModalContent>

      <StyledButtonGroup>
        <Button variant='ghost' size='small' disabled={isPending} onClick={() => setShow(false)}>
          Cancel
        </Button>
        <Button variant='primary' size='small' onClick={handleAdd} disabled={selectedPlans.length === 0 || isPending}>
          Add Contracted Health Plans
        </Button>
      </StyledButtonGroup>
    </StandardModal>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export default AddAcceptedPlanModal;
